import React from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';

const FeeComparison = () => {
  // Traditional processing - Month 1
  const tradVolume = 43356.70;
  const feeRate = 3.99;
  const tradFees = (tradVolume * feeRate / 100).toFixed(2);

  // Stackably Dual Pricing - Month 2 (different volume, realistic)
  const stackVolume = 45821.35;
  const stackWouldPayFees = (stackVolume * feeRate / 100).toFixed(2);

  // Average monthly savings for annual projection
  const avgMonthlyFees = ((Number(tradFees) + Number(stackWouldPayFees)) / 2).toFixed(2);
  const annualSavings = (Number(avgMonthlyFees) * 12).toFixed(2);

  return (
    <section className="py-20" style={{ backgroundColor: '#0B1F3A' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            See the <span style={{ color: '#D4AF37' }}>Difference</span> for Yourself
          </h2>
          <p className="text-lg text-gray-300">Real numbers from a real business. Same business, two different months — completely different outcome.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          {/* Traditional Processing Statement - January */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl">
            <div className="px-6 py-4 flex items-center gap-2" style={{ backgroundColor: '#DC2626' }}>
              <TrendingDown className="text-white" size={20} />
              <span className="text-white font-bold text-lg">Traditional Processing</span>
            </div>
            <div className="p-6">
              <div className="border-b border-gray-200 pb-4 mb-4">
                <p className="font-bold text-lg" style={{ color: '#0F172A' }}>Washington Inn</p>
                <div className="mt-1 h-4 w-48 rounded" style={{ backgroundColor: '#E5E7EB', filter: 'blur(4px)' }}></div>
                <div className="mt-1 h-4 w-36 rounded" style={{ backgroundColor: '#E5E7EB', filter: 'blur(4px)' }}></div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Statement Period</span>
                  <span className="font-medium" style={{ color: '#0F172A' }}>Jan 1 - Jan 31, 2026</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Account Number</span>
                  <span className="font-medium" style={{ filter: 'blur(4px)', color: '#0F172A' }}>****-****-7291</span>
                </div>
                <div className="border-t border-gray-100 pt-3"></div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Card Volume</span>
                  <span className="font-bold" style={{ color: '#0F172A' }}>${tradVolume.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Effective Rate</span>
                  <span className="font-bold" style={{ color: '#DC2626' }}>{feeRate}%</span>
                </div>
                <div className="border-t border-gray-100 pt-3"></div>
                <div className="flex justify-between items-center bg-red-50 rounded-lg px-4 py-3 -mx-2">
                  <span className="font-semibold text-base" style={{ color: '#DC2626' }}>Total Fees Charged</span>
                  <span className="font-bold text-xl" style={{ color: '#DC2626' }}>-${Number(tradFees).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-3 w-full rounded" style={{ backgroundColor: '#F3F4F6', filter: 'blur(3px)' }}></div>
                <div className="h-3 w-3/4 rounded" style={{ backgroundColor: '#F3F4F6', filter: 'blur(3px)' }}></div>
                <div className="h-3 w-5/6 rounded" style={{ backgroundColor: '#F3F4F6', filter: 'blur(3px)' }}></div>
              </div>
            </div>
          </div>

          {/* Stackably Dual Pricing Statement - February */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border-2" style={{ borderColor: '#22C55E' }}>
            <div className="px-6 py-4 flex items-center gap-2" style={{ backgroundColor: '#22C55E' }}>
              <TrendingUp className="text-white" size={20} />
              <span className="text-white font-bold text-lg">Stackably Dual Pricing</span>
            </div>
            <div className="p-6">
              <div className="border-b border-gray-200 pb-4 mb-4">
                <p className="font-bold text-lg" style={{ color: '#0F172A' }}>Washington Inn</p>
                <div className="mt-1 h-4 w-48 rounded" style={{ backgroundColor: '#E5E7EB', filter: 'blur(4px)' }}></div>
                <div className="mt-1 h-4 w-36 rounded" style={{ backgroundColor: '#E5E7EB', filter: 'blur(4px)' }}></div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Statement Period</span>
                  <span className="font-medium" style={{ color: '#0F172A' }}>Feb 1 - Feb 28, 2026</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Account Number</span>
                  <span className="font-medium" style={{ filter: 'blur(4px)', color: '#0F172A' }}>****-****-7291</span>
                </div>
                <div className="border-t border-gray-100 pt-3"></div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Card Volume</span>
                  <span className="font-bold" style={{ color: '#0F172A' }}>${stackVolume.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Effective Rate</span>
                  <span className="font-bold" style={{ color: '#22C55E' }}>0.00%</span>
                </div>
                <div className="border-t border-gray-100 pt-3"></div>
                <div className="flex justify-between items-center rounded-lg px-4 py-3 -mx-2" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                  <span className="font-semibold text-base" style={{ color: '#22C55E' }}>Total Fees Charged</span>
                  <span className="font-bold text-xl" style={{ color: '#22C55E' }}>$0.00</span>
                </div>
              </div>
              <div className="mt-4 rounded-xl p-4 text-center" style={{ backgroundColor: 'rgba(34, 197, 94, 0.08)', border: '1px dashed #22C55E' }}>
                <p className="text-sm text-gray-500 mb-1">You keep</p>
                <p className="text-3xl font-bold" style={{ color: '#22C55E' }}>${Number(stackWouldPayFees).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                <p className="text-sm font-medium mt-1" style={{ color: '#22C55E' }}>every single month</p>
              </div>
            </div>
          </div>
        </div>

        {/* Annual savings */}
        <div className="text-center mt-10">
          <div className="inline-flex flex-col items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl px-10 py-6">
            <span className="text-gray-400 text-sm uppercase tracking-wider">Estimated Annual Savings</span>
            <span className="text-4xl font-bold" style={{ color: '#D4AF37' }}>${Number(annualSavings).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            <span className="text-gray-400 text-sm">back in your pocket every year</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeeComparison;
